import { useEffect, useState, useContext, useMemo, useCallback } from 'react'
import { Delete, Edit, AddToPhotos } from '@mui/icons-material'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import { CircularProgress, TableCell, TableRow, Checkbox, Tooltip, Fab } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Component as HeaderIcon } from 'lucide-react'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { Paper, TableCellTooltip } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { patternContext, modalContext } from '@contexts'
import { colors } from '@global/constants'
import { useQueryString } from '@global/hooks'
import { api } from 'api'
import { CvsTable } from '../../components/table/cvs-table.jsx'
import { EnhancedTableToolbar } from '../../components/table/enhanced-table-toolbar'
import * as SnackbarActions from '../../global/snackbar/snackbar.actions'
import { Fuzzy } from '../../utils/fuse-search'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

function getCellContent(type, value) {
  switch (type) {
    case 'text':
      return value
    case 'number':
      return Intl.NumberFormat().format(value)
    case 'date':
      return value ? moment(value).format('MM/DD/YY') : t`Never`
    default:
      return value
  }
}

export const Patterns = ({ history }) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const ModalContext = useContext(modalContext)
  const { byId, fetchPatterns, updatePatterns } = useContext(patternContext)
  const [loaded, setLoaded] = useState(false)
  const [selected, setSelected] = useState({})
  const patterns = useMemo(() => Object.keys(byId).map(id => byId[id]), [byId])
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(history.location.search).get('q') || '',
  )
  const { setUrl } = useQueryString(searchTerm, setSearchTerm)

  const columns = [
    { label: t`Name`, width: '40%', lookup: 'name' },
    { label: t`Updated Date`, width: '20%', lookup: 'updated_at' },
    { label: t`Description`, width: '40%', lookup: 'description' },
  ]

  useEffect(() => {
    setLoaded(false)
    fetchPatterns().then(() => setLoaded(true))
  }, [fetchPatterns])

  const fuzzy = useMemo(() => new Fuzzy(patterns, { keys: ['name'] }), [patterns])

  const displayPatterns = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return patterns
    }

    return fuzzy.search(searchTerm)
  }, [patterns, searchTerm, fuzzy])

  const filterChanged = useCallback(search => setUrl({ q: search }))

  const duplicateItem = () => {
    const edit = Object.keys(selected)[0]
    history.push(`/admin/patterns/new?duplicate=${edit}`)
  }

  const editItems = editItem => {
    const edit = editItem && editItem.id ? editItem.id : Object.keys(selected)[0]
    history.push(`/admin/patterns/${edit}`)
  }

  const addItem = () => {
    history.push('/admin/patterns/new')
  }

  const deleteItems = payload => {
    api
      .post('/api/v1/account/field/patterns/bulk/delete', {
        pattern_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        const filteredPatterns = patterns.filter(p => !payload[p.id])
        updatePatterns(filteredPatterns)
        setSelected({})
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Pattern deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Pattern failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these Patterns`,
      description: t`Patterns are an integral part of the Claravine experience and submission templates may depend on them. Please be sure this is what you'd like to do as this action can't be undone.`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Patterns`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Manage the taxonomy that will be used to generate your submission tracking codes`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loaded ? (
          <div className={classes.table} style={{ height: 'calc(100% - 105px)' }}>
            <EnhancedTableToolbar
              term={searchTerm}
              numSelected={numSelected}
              filterChanged={filterChanged}
              addItem={addItem}
              editItems={editItems}
              deleteItems={deleteItems}
              noAdd={false}
              blockMultipleEdit={false}
              duplicateItem={duplicateItem}
            >
              <Tooltip title={t`Duplicate`}>
                <Fab
                  style={{
                    color: 'white',
                    marginRight: '10px',
                  }}
                  color="secondary"
                  size="small"
                  aria-label={t`Duplicate`}
                  className={classes.fab}
                  disabled={numSelected > 1}
                  onClick={duplicateItem}
                >
                  <AddToPhotos />
                </Fab>
              </Tooltip>
              <Tooltip title={t`Edit`}>
                <Fab
                  color="primary"
                  style={{ color: 'white', marginRight: '10px' }}
                  size="small"
                  aria-label={t`Add`}
                  className={classes.fab}
                  disabled={numSelected > 1}
                  onClick={editItems}
                >
                  <Edit />
                </Fab>
              </Tooltip>
              <Tooltip
                title={t`Delete`}
                style={{ backgroundColor: colors.red.light, color: 'white' }}
              >
                <Fab
                  color="inherit"
                  size="small"
                  aria-label={t`Add`}
                  className={classes.fab}
                  onClick={deleteItemsModal}
                >
                  <Delete />
                </Fab>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                columns={columns}
                rows={displayPatterns}
                orderBy="updated_at"
                sortOrder="desc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination
                style={{ maxWidth: '100%' }}
                multiSelect
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <TimelineOutlinedIcon
                        style={{ fontSize: '34px', color: colors.blue.smokey }}
                      />
                    </Fab>
                  ),
                  title: t`No Patterns Found.`,
                  description: t`Try a different search or add a new pattern.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayPatterns.reduce((memo, item) => {
                      memo[item.id] = val
                      return memo
                    }, {}),
                  )
                }}
              >
                {({ row }) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => {
                        numSelected
                          ? setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          : editItems(row)
                      }}
                      tabIndex={-1}
                      key={row.id}
                      style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={e => {
                            e.stopPropagation()
                            setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          }}
                          checked={selected[row.id]}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('date', row.updated_at)}
                      </TableCell>
                      <TableCellTooltip title={row.description}>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {getCellContent('text', row.description)}
                        </TableCell>
                      </TableCellTooltip>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
    </motion.div>
  )
}
